import React, { useState } from 'react';
import { BottomSheet } from './Modal';
import { MessageCircle, Send, Facebook, Link2, Check } from 'lucide-react';
import clsx from 'clsx';

/**
 * ShareMenu — single, on-brand share sheet for the whole app.
 *
 * Previously this was duplicated in App.js, Navbar.jsx and FooterNav.jsx
 * with three different styles. Now: one component, one look.
 *
 * <ShareMenu
 *   isOpen={open}
 *   onClose={() => setOpen(false)}
 *   url="https://hamekomit.com"
 *   title="בואו לבקר במקומית"
 *   description="שתף את האפליקציה"
 * />
 */

const TARGETS = [
    {
        key: 'whatsapp',
        label: 'וואטסאפ',
        Icon: MessageCircle,
        accent: 'bg-success-50 text-success-700 hover:bg-success-100 border-success-200/60',
        href: ({ url, title }) => `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    },
    {
        key: 'telegram',
        label: 'טלגרם',
        Icon: Send,
        accent: 'bg-info-50 text-info-700 hover:bg-info-100 border-info-200/60',
        href: ({ url, title }) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    },
    {
        key: 'facebook',
        label: 'פייסבוק',
        Icon: Facebook,
        accent: 'bg-info-50 text-info-700 hover:bg-info-100 border-info-200/60',
        href: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
];

export function ShareMenu({
    isOpen,
    onClose,
    url = 'https://hamekomit.com',
    title = 'בואו לבקר במקומית',
    description,
    heading = 'שתף',
}) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // Fallback for older browsers
            const ta = document.createElement('textarea');
            ta.value = url;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
            document.body.removeChild(ta);
        }
    };

    return (
        <BottomSheet
            isOpen={isOpen}
            onClose={onClose}
            title={heading}
            subtitle={description}
            showCloseButton
        >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {TARGETS.map(({ key, label, Icon, accent, href }) => (
                    <a
                        key={key}
                        href={href({ url, title })}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={onClose}
                        className={clsx(
                            'flex flex-col items-center justify-center gap-2 py-4 px-3',
                            'rounded-xl border transition-[background-color,transform,box-shadow] duration-200 ease-smooth',
                            'hover:-translate-y-0.5 hover:shadow-soft active:scale-[0.98]',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                            accent,
                        )}
                    >
                        <span className="inline-flex items-center justify-center w-11 h-11 rounded-pill bg-white/80">
                            <Icon className="w-5 h-5" />
                        </span>
                        <span className="text-sm font-semibold">{label}</span>
                    </a>
                ))}

                <button
                    type="button"
                    onClick={handleCopy}
                    className={clsx(
                        'flex flex-col items-center justify-center gap-2 py-4 px-3 col-span-2 sm:col-span-4',
                        'rounded-xl border transition-[background-color,transform,box-shadow] duration-200 ease-smooth',
                        'hover:-translate-y-0.5 hover:shadow-soft active:scale-[0.98]',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                        copied
                            ? 'bg-success-50 text-success-700 border-success-200'
                            : 'bg-surface-muted text-ink border-line hover:bg-surface-sunken',
                    )}
                    aria-live="polite"
                >
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-pill bg-white/80">
                        {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
                    </span>
                    <span className="text-sm font-semibold">
                        {copied ? 'הקישור הועתק' : 'העתק קישור'}
                    </span>
                </button>
            </div>

            <p className="mt-5 text-xs text-ink-subtle text-center break-all">
                {url}
            </p>
        </BottomSheet>
    );
}

export default ShareMenu;
